/**
 * Invoice Recipients Service
 * Manages the list of email addresses that receive invoices for a client.
 */

import { makeAuthenticatedRequest, createApiError } from './apiService';
import { API_ENDPOINTS } from '../config/api';

// --------------------------------------------------------------------------
// Types
// --------------------------------------------------------------------------

export interface InvoiceRecipient {
  id: string;
  email: string;
  createdAt: string;
}

// --------------------------------------------------------------------------
// Service functions
// --------------------------------------------------------------------------

/**
 * Fetch all invoice recipients for the given client.
 * Auto-seeds from the client's ContactEmail if the list is currently empty.
 */
export const getInvoiceRecipients = async (clientId: string): Promise<InvoiceRecipient[]> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.INVOICE_RECIPIENTS.LIST(clientId));

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return response.json();
};

/**
 * Add a new recipient email address for the given client.
 */
export const addInvoiceRecipient = async (
  clientId: string,
  email: string
): Promise<InvoiceRecipient> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.INVOICE_RECIPIENTS.ADD(clientId), {
    method: 'POST',
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return response.json();
};

/**
 * Remove a recipient from the given client's invoice recipient list.
 */
export const removeInvoiceRecipient = async (
  clientId: string,
  recipientId: string
): Promise<void> => {
  const response = await makeAuthenticatedRequest(
    API_ENDPOINTS.INVOICE_RECIPIENTS.REMOVE(clientId, recipientId),
    { method: 'DELETE' }
  );

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }
};
